import React, { useEffect, useState, useRef } from "react";
import { FaTrash, FaArrowLeft, FaPlus, FaSearch, FaVolumeUp, FaGripVertical, FaList, FaTimes, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { FaPencil } from "react-icons/fa6";
import { motion, AnimatePresence } from 'framer-motion';
import ApiService from "../models/ApiService";

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Vocabulary Item Component
const SortableVocabularyItem = ({ item, onEdit, onDelete, onPlayAudio }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: item._id || `temp-${item.no}-${item.word}`, // Use _id as primary key
    data: {
      type: 'vocabulary',
      item
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-md border border-[--border-color] hover:shadow-lg transition-all duration-300 cursor-move ${isDragging ? 'shadow-xl ring-2 ring-[--primary-btn-color] scale-105' : ''
        }`}
    >
      <div className="p-6">
        {/* Word and Audio */}
        <div className="mb-4 flex justify-between items-start">
          <button
            onClick={() => onPlayAudio(item.voice_note_url)}
            className={`p-2 rounded-full ${item.voice_note_url
              ? "text-[--primary-btn-color] hover:bg-blue-50"
              : "text-gray-300 cursor-not-allowed"
              }`}
            // disabled={!item.voice_note}
            title={item.voice_note_url ? "Play pronunciation" : "No audio available"}
          >
            <FaVolumeUp className="text-xl" />
          </button>
          <div>
            <h3
              className="text-2xl font-bold text-[--text-color-g] text-right mb-4"
              id="primary-urdu-fonts"
            >
              {item.word}
            </h3>
            <p className="text-sm text-[--text-color] font-bold text-right">
              {item.transliteration}
            </p>
            {item.devanagari_transliteration && (
              <p className="text-sm text-[--text-color] font-bold text-right mt-1">
                {item.devanagari_transliteration}
              </p>
            )}
          </div>
        </div>

        {/* Meaning */}
        <div className="mb-4">
          <p className="text-[--text-color-g] font-medium mb-2">
            {item.meaning}
          </p>
          {item.hindi_meaning && (
            <p className="text-sm text-gray-600">
              <strong>Hindi:</strong> {item.hindi_meaning}
            </p>
          )}
          {item.english_name && (
            <p className="text-sm text-gray-600">
              <strong>English:</strong> {item.english_name}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={() => onEdit(item)}
            className="p-2 text-[--primary-btn-color] rounded-full hover:bg-gray-100 transition-colors"
            title="Edit"

          >
            <FaPencil />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="p-2 text-red-600 rounded-full hover:bg-gray-100 transition-colors"
            title="Delete"

          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
};

// Order Management Modal Component
const OrderManagementModal = ({ isOpen, onClose, items, categoryName, onReorder }) => {
  const [orderedItems, setOrderedItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [swapWithIndex, setSwapWithIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (items && isOpen) {
      setOrderedItems([...items].sort((a, b) => (a.order_no || a.no) - (b.order_no || b.no)));
    }
  }, [items, isOpen]);

  const moveUp = (index) => {
    if (index > 0) {
      const newOrder = [...orderedItems];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];

      const updated = newOrder.map((item, idx) => ({
        ...item,
        order_no: idx + 1,
        no: idx + 1
      }));

      setOrderedItems(updated);
      setOrderedItems(newOrder);
      setSelectedIndex(index - 1);
      setSwapWithIndex(null);
    }
  };

  const moveDown = (index) => {
    if (index < orderedItems.length - 1) {
      const newOrder = [...orderedItems];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      newOrder.forEach((item, idx) => {
        item.order_no = idx + 1;
        item.no = idx + 1;
      });
      setOrderedItems(newOrder);
      setSelectedIndex(index + 1);
      setSwapWithIndex(null);
    }
  };

  const moveToTop = (index) => {
    if (index > 0) {
      const newOrder = [...orderedItems];
      const [item] = newOrder.splice(index, 1);
      newOrder.unshift(item);
      newOrder.forEach((item, idx) => {
        item.order_no = idx + 1;
        item.no = idx + 1;
      });
      setOrderedItems(newOrder);
      setSelectedIndex(0);
      setSwapWithIndex(null);
    }
  };

  const moveToBottom = (index) => {
    if (index < orderedItems.length - 1) {
      const newOrder = [...orderedItems];
      const [item] = newOrder.splice(index, 1);
      newOrder.push(item);
      newOrder.forEach((item, idx) => {
        item.order_no = idx + 1;
        item.no = idx + 1;
      });
      setOrderedItems(newOrder);
      setSelectedIndex(orderedItems.length - 1);
      setSwapWithIndex(null);
    }
  };

  const handleSwap = (index1, index2) => {
    if (index1 === index2) return;

    const newOrder = [...orderedItems];
    [newOrder[index1], newOrder[index2]] = [newOrder[index2], newOrder[index1]];

    newOrder.forEach((item, idx) => {
      item.order_no = idx + 1;
      item.no = idx + 1;
    });

    setOrderedItems(newOrder);
    setSelectedIndex(index2);
    setSwapWithIndex(null);
  };

  const handleItemClick = (index) => {
    if (swapWithIndex === null) {
      setSwapWithIndex(index);
      setSelectedIndex(index);
    } else {
      if (swapWithIndex !== index) {
        handleSwap(swapWithIndex, index);
      }
      setSwapWithIndex(null);
    }
  };

  const clearSwapSelection = () => {
    setSwapWithIndex(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = orderedItems.map((item, idx) => ({
        _id: item._id,
        no: idx + 1,
        order_no: idx + 1
      }));
      await onReorder(payload);
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setOrderedItems([...items].sort((a, b) => (a.order_no || a.no) - (b.order_no || b.no)));
    setSelectedIndex(null);
    setSwapWithIndex(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    // <AnimatePresence>
    //   <motion.div
    //     initial={{ opacity: 0 }}
    //     animate={{ opacity: 1 }}
    //     exit={{ opacity: 0 }}
    //     className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    //   >
    //     <motion.div
    //       initial={{ scale: 0.95, opacity: 0 }}
    //       animate={{ scale: 1, opacity: 1 }}
    //       exit={{ scale: 0.95, opacity: 0 }}
    //       transition={{ duration: 0.25 }}
    //       className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
    //     >
    //       <div className="p-6 border-b border-gray-200 flex justify-between items-center">
    //         <div>
    //           <h2 className="text-2xl font-bold text-[--text-color]">
    //             Manage Order - {categoryName}
    //           </h2>
    //           <p className="text-sm text-gray-500 mt-1">
    //             Total Words: {orderedItems.length}
    //           </p>
    //           {swapWithIndex !== null && (
    //             <p className="text-sm text-blue-600 mt-1">
    //               Selected item #{swapWithIndex + 1}. Click on another item to swap positions.
    //               <button
    //                 onClick={clearSwapSelection}
    //                 className="ml-2 text-red-500 hover:text-red-700 underline"
    //               >
    //                 Cancel
    //               </button>
    //             </p>
    //           )}
    //         </div>
    //         <button
    //           onClick={handleCancel}
    //           className="p-2 hover:bg-gray-100 rounded-full transition-colors"
    //         >
    //           <FaTimes className="text-gray-500 text-xl" />
    //         </button>
    //       </div>

    //       <div className="flex-1 overflow-y-auto p-6">
    //         <div className="space-y-3">
    //           {orderedItems.map((item, index) => (
    //             <motion.div
    //               key={item._id}
    //               initial={{ opacity: 0, y: 20 }}
    //               animate={{ opacity: 1, y: 0 }}
    //               transition={{ delay: index * 0.05 }}
    //               className={`bg-white border rounded-lg p-4 transition-all duration-200 cursor-pointer $cursor-pointer ${selectedIndex === index
    //                 ? 'border-[--border-color-g] shadow-lg '
    //                 : 'border-gray-200 hover:shadow-md'
    //                 } ${swapWithIndex === index
    //                   ? 'ring-1 ring-[--border-color-g] bg-blue-50'
    //                   : ''
    //                 }`}
    //             // onClick={() => handleItemClick(index)}
    //             >
    //               <div className="flex items-start gap-4">
    //                 {/* Order Number and Controls */}
    //                 <div className="flex-shrink-0 w-28">
    //                   <div className="text-center">
    //                     <span className={`inline-block rounded-lg px-3 py-1 text-sm font-semibold ${swapWithIndex === index
    //                       ? 'bg-[--primary-color] text-white'
    //                       : 'bg-gray-100 text-gray-700'
    //                       }`}>
    //                       #{item.order_no || item.no || index + 1}
    //                     </span>
    //                   </div>

    //                 </div>

    //                 {/* Content Preview */}
    //                 <div className="flex-1">
    //                   <div className="flex justify-between items-start">
    //                     <div className="flex-1 ">
    //                       <h3 className="text-xl font-bold text-[--text-color-g]" id="primary-urdu-fonts">
    //                         {item.word}
    //                       </h3>
    //                       <p className="text-sm text-gray-600 mt-1">
    //                         {item.transliteration}
    //                       </p>
    //                       {item.devanagari_transliteration && (
    //                         <p className="text-sm text-gray-600">
    //                           {item.devanagari_transliteration}
    //                         </p>
    //                       )}
    //                     </div>
    //                   </div>
    //                   {/* <div className="mt-2">
    //                     <p className="text-sm text-gray-700">
    //                       {item.meaning?.substring(0, 80)}
    //                       {item.meaning?.length > 80 && '...'}
    //                     </p>
    //                   </div> */}
    //                 </div>

    //                 {/* Quick Actions */}
    //                 <div className="flex-shrink-0">
    //                   <div className="flex gap-1">
    //                     <button
    //                       onClick={(e) => {
    //                         e.stopPropagation();
    //                         moveToTop(index);
    //                       }}
    //                       disabled={index === 0}
    //                       className={`px-2 py-1 text-xs rounded transition-colors ${index === 0
    //                         ? 'text-gray-300 cursor-not-allowed'
    //                         : 'text-[--text-color-g] hover:bg-gray-100'
    //                         }`}
    //                       title="Move to Top"
    //                     >
    //                       Top
    //                     </button>
    //                     <button
    //                       onClick={(e) => {
    //                         e.stopPropagation();
    //                         moveToBottom(index);
    //                       }}
    //                       disabled={index === orderedItems.length - 1}
    //                       className={`px-2 py-1 text-xs rounded transition-colors ${index === orderedItems.length - 1
    //                         ? 'text-gray-300 cursor-not-allowed'
    //                         : 'text-[--text-color-g] hover:bg-gray-100'
    //                         }`}
    //                       title="Move to Bottom"
    //                     >
    //                       Bottom
    //                     </button>
    //                     <button
    //                       onClick={(e) => {
    //                         e.stopPropagation();
    //                         if (swapWithIndex !== null && swapWithIndex !== index) {
    //                           handleSwap(swapWithIndex, index);
    //                           setSwapWithIndex(null);
    //                         } else if (swapWithIndex === null) {
    //                           setSwapWithIndex(index);
    //                           setSelectedIndex(index);
    //                         } else if (swapWithIndex === index) {
    //                           setSwapWithIndex(null);
    //                         }
    //                       }}
    //                       className={`px-2 py-1 text-xs rounded transition-colors ${swapWithIndex === index
    //                         ? 'bg-[--primary-color] text-white'
    //                         : 'text-[--text-color-g] hover:bg-gray-100'
    //                         }`}
    //                       title="Swap with another word"
    //                     >
    //                       Swap
    //                     </button>
    //                   </div>

    //                   <div className="flex flex-col gap-1 mt-2">
    //                     <button
    //                       onClick={(e) => {
    //                         e.stopPropagation();
    //                         moveUp(index);
    //                       }}
    //                       disabled={index === 0}
    //                       className={`p-1 rounded text-center text-sm transition-colors ${index === 0
    //                         ? 'text-gray-300 cursor-not-allowed'
    //                         : 'text-[--text-color] hover:bg-blue-50'
    //                         }`}
    //                       title="Move Up"
    //                     >
    //                       <FaArrowUp className="inline mr-1" size={12} /> Up
    //                     </button>
    //                     <button
    //                       onClick={(e) => {
    //                         e.stopPropagation();
    //                         moveDown(index);
    //                       }}
    //                       disabled={index === orderedItems.length - 1}
    //                       className={`p-1 rounded text-center text-sm transition-colors ${index === orderedItems.length - 1
    //                         ? 'text-gray-300 cursor-not-allowed'
    //                         : 'text-[--text-color] hover:bg-blue-50'
    //                         }`}
    //                       title="Move Down"
    //                     >
    //                       <FaArrowDown className="inline mr-1" size={12} /> Down
    //                     </button>
    //                   </div>
    //                 </div>
    //               </div>
    //             </motion.div>
    //           ))}
    //         </div>
    //       </div>

    //       <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
    //         <button
    //           onClick={handleCancel}
    //           className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
    //         >
    //           Cancel
    //         </button>
    //         <button
    //           onClick={handleSave}
    //           disabled={isSaving}
    //           className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition-colors disabled:opacity-50"
    //         >
    //           {isSaving ? 'Saving...' : 'Save Order'}
    //         </button>
    //       </div>
    //     </motion.div>
    //   </motion.div>
    // </AnimatePresence>

    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        >
          {/* Header - Compact */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-[--text-color]">
                Manage Order - {categoryName}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Total Words: {orderedItems.length}
                {swapWithIndex !== null && (
                  <span className="ml-2 text-blue-600">
                    • Selected item #{swapWithIndex + 1}. Click another item to swap.
                    <button
                      onClick={clearSwapSelection}
                      className="ml-2 text-red-500 hover:text-red-700 underline"
                    >
                      Cancel
                    </button>
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaTimes className="text-gray-500 text-lg" />
            </button>
          </div>

          {/* Content - Optimized spacing */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-2">
              {orderedItems.map((item, index) => (
                <motion.div
                  key={item._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white border rounded-lg p-3 transition-all duration-200 ${selectedIndex === index
                    ? 'border-[--border-color-g] shadow-lg'
                    : 'border-gray-200 hover:shadow-md'
                    } ${swapWithIndex === index
                      ? 'ring-1 ring-[--border-color-g] bg-blue-50'
                      : ''
                    }`}
                >
                  <div className="flex items-center  gap-3">
                    {/* Order Number - Compact */}
                    <div className="flex-shrink-0 w-20">
                      <div className="text-center">
                        <span className={`inline-block rounded-lg px-2 py-0.5 text-xs font-semibold ${swapWithIndex === index
                          ? 'bg-[--primary-color] text-white'
                          : 'bg-gray-100 text-gray-700'
                          }`}>
                          #{index + 1}
                        </span>
                      </div>
                    </div>

                    {/* Content Preview - Compact */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold p-1 text-[--text-color-g] truncate" id="primary-urdu-fonts">
                        {item.word}
                      </h3>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600 mt-0.5">
                        <span className="truncate">{item.transliteration}</span>
                        {item.devanagari_transliteration && (
                          <span className="truncate">{item.devanagari_transliteration}</span>
                        )}
                      </div>
                    </div>

                    {/* Quick Actions - Compact horizontal layout */}
                    <div className="flex-shrink-0 flex gap-2">
                      {/* Move buttons group */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveUp(index);
                          }}
                          disabled={index === 0}
                          className={`px-2 py-0.5 rounded text-xs transition-colors flex items-center gap-1 ${index === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-[--text-color] hover:bg-blue-50'
                            }`}
                          title="Move Up"
                        >
                          <FaArrowUp size={10} /> Up
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveDown(index);
                          }}
                          disabled={index === orderedItems.length - 1}
                          className={`px-2 py-0.5 rounded text-xs transition-colors flex items-center gap-1 ${index === orderedItems.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-[--text-color] hover:bg-blue-50'
                            }`}
                          title="Move Down"
                        >
                          <FaArrowDown size={10} /> Down
                        </button>
                      </div>

                      {/* Position buttons group */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveToTop(index);
                          }}
                          disabled={index === 0}
                          className={`px-2 py-0.5 rounded text-xs transition-colors ${index === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-[--text-color-g] hover:bg-gray-100'
                            }`}
                          title="Move to Top"
                        >
                          Top
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveToBottom(index);
                          }}
                          disabled={index === orderedItems.length - 1}
                          className={`px-2 py-0.5 rounded text-xs transition-colors ${index === orderedItems.length - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-[--text-color-g] hover:bg-gray-100'
                            }`}
                          title="Move to Bottom"
                        >
                          Bottom
                        </button>
                      </div>

                      {/* Swap button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (swapWithIndex !== null && swapWithIndex !== index) {
                            handleSwap(swapWithIndex, index);
                            setSwapWithIndex(null);
                          } else if (swapWithIndex === null) {
                            setSwapWithIndex(index);
                            setSelectedIndex(index);
                          } else if (swapWithIndex === index) {
                            setSwapWithIndex(null);
                          }
                        }}
                        className={`px-2 py-2 rounded text-xs transition-colors self-center ${swapWithIndex === index
                          ? 'bg-[--primary-color] text-white'
                          : 'text-[--text-color-g] hover:bg-gray-100'
                          }`}
                        title="Swap with another word"
                      >
                        Swap
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Footer - Compact */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function Vocabulary() {
  const [vocabularyList, setVocabularyList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newWord, setNewWord] = useState({
    category: "",
    vocabulary_id: "",
    word: "",
    transliteration: "",
    devanagari_transliteration: "",
    english_name: "",
    hindi_meaning: "",
    voice_note: "",
    meaning: "",
  });
  const [audioFile, setAudioFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingOrder, setPendingOrder] = useState([]);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const audioRef = useRef(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter data based on selected category and search term
  const filteredData = selectedCategory
    ? vocabularyList
      .filter((item) => item.category === selectedCategory.vocabulary_category)
      .filter((item) =>
        item.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.transliteration.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.meaning.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.devanagari_transliteration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.english_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.hindi_meaning?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => (a.order_no || a.no) - (b.order_no || b.no))
    : [];

  // Filter categories based on search term when in category view
  const filteredCategories = categories.filter(category =>
    category.vocabulary_category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewWord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('audio/')) {
        setError("Please select an audio file (MP3, WAV, etc.)");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError("File size should be less than 10MB");
        return;
      }
      setAudioFile(file);
      setError(null);
    }
  };

  useEffect(() => {
    fetchVocabulary();
    fetchVocabularyCategories();

    audioRef.current = new Audio();
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const fetchVocabulary = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await ApiService.GetVocabulary();
      console.log("API Response:", res);

      const flatData = res.data.responseContent.reduce((acc, categoryGroup) => {
        return [
          ...acc,
          ...categoryGroup.data.map((item, idx) => ({
            ...item,
            category: categoryGroup.category,
            order_no: item.no || idx + 1
          })),
        ];
      }, []);

      setVocabularyList(flatData || []);
    } catch (error) {
      console.error("Failed to fetch vocabulary:", error);
      setError("Failed to load vocabulary. Please try again later.");
      setVocabularyList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVocabularyCategories = async () => {
    try {
      const res = await ApiService.GetVocabularyCategory();
      console.log("Categories response:", res);
      if (res.data && res.data.responseContent) {
        setCategories(res.data.responseContent);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setError("Failed to load categories. Please try again later.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsUploading(true);

    try {
      const data = new FormData();
      data.append("category", newWord.category);
      data.append("vocabulary_id", newWord.vocabulary_id);
      data.append("word", newWord.word);
      data.append("transliteration", newWord.transliteration);
      data.append("devanagari_transliteration", newWord.devanagari_transliteration);
      data.append("english_name", newWord.english_name);
      data.append("hindi_meaning", newWord.hindi_meaning);
      data.append("meaning", newWord.meaning);

      if (!editingId) {
        const categoryItems = vocabularyList.filter(item => item.category === newWord.category);
        data.append("no", categoryItems.length + 1);
      }

      if (audioFile) {
        data.append("voice_note", audioFile);
      }

      if (editingId !== null) {
        await ApiService.UpdateVocabulary(data, editingId);
      } else {
        await ApiService.AddVocabulary(data);
      }

      await fetchVocabulary();
      setShowModal(false);
      setEditingId(null);
      setAudioFile(null);
      resetForm();
    } catch (error) {
      console.error("Failed to save vocabulary:", error);
      setError("Failed to save. Please try again.");
    } finally {
      setIsLoading(false);
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setNewWord({
      category: "",
      vocabulary_id: "",
      word: "",
      transliteration: "",
      devanagari_transliteration: "",
      english_name: "",
      hindi_meaning: "",
      voice_note: "",
      meaning: "",
    });
  };

  const handleEdit = (vocabularyItem) => {
    setEditingId(vocabularyItem.no);
    setNewWord({
      category: vocabularyItem.category,
      word: vocabularyItem.word,
      transliteration: vocabularyItem.transliteration,
      devanagari_transliteration: vocabularyItem.devanagari_transliteration || "",
      english_name: vocabularyItem.english_name || "",
      hindi_meaning: vocabularyItem.hindi_meaning || "",
      meaning: vocabularyItem.meaning || "",
      voice_note: vocabularyItem.voice_note || "",
      vocabulary_id: vocabularyItem.vocabulary_category_id,
    });
    setAudioFile(null);
    setShowModal(true);
  };

  const handleDelete = async (vocabularyItem) => {
    if (window.confirm("Are you sure you want to delete this word?")) {
      setIsLoading(true);
      try {
        await ApiService.DeleteVocabulary(vocabularyItem.no);
        await fetchVocabulary();
      } catch (error) {
        console.error("Failed to delete vocabulary:", error);
        setError("Failed to delete. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const playAudio = async (audioUrl) => {
    if (!audioUrl || !audioRef.current) return;

    const audio = audioRef.current;
    try {
      audio.src = audioUrl;
      audio.load();
      await audio.play();
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Audio error:", err);
        setError("Could not play audio");
      }
    }
  };

  const getCategoryWordCount = (categoryName) => {
    return vocabularyList.filter(item => item.category === categoryName).length;
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchTerm("");
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSearchTerm("");
    setHasOrderChanges(false);
    setPendingOrder([]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleDragStart = (event) => {
    // Optional: Add drag start logic
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredData.findIndex(
      item => (item._id || `temp-${item.no}-${item.word}`) === active.id
    );
    const newIndex = filteredData.findIndex(
      item => (item._id || `temp-${item.no}-${item.word}`) === over.id
    );

    const reordered = arrayMove(filteredData, oldIndex, newIndex);

    // Update order numbers
    const updatedItems = reordered.map((item, idx) => ({
      ...item,
      no: idx + 1,
      order_no: idx + 1
    }));

    // Update local state
    const updatedVocabularyList = vocabularyList.map(item => {
      if (item.category === selectedCategory?.vocabulary_category) {
        const updated = updatedItems.find(u => u._id === item._id);
        return updated || item;
      }
      return item;
    });

    setVocabularyList(prev => {
      const updated = prev.map(item => {

        return item;
      });

      // 🔥 FORCE SORT AFTER UPDATE
      return updated.sort((a, b) => {
        if (a.category === selectedCategory?.vocabulary_category) {
          return (a.order_no || a.no) - (b.order_no || b.no);
        }
        return 0;
      });
    });
    // Prepare payload for API - use _id
    const payload = updatedItems.map(item => ({
      _id: item._id,
      no: item.no,
      order_no: item.order_no
    }));

    setPendingOrder(payload);
    setHasOrderChanges(true);
  };

  const handleConfirmOrder = async () => {
    if (!pendingOrder.length) return;

    try {
      setIsReordering(true);
      // Call API to update order
      await ApiService.UpdateVocabularyNo(pendingOrder)
      setHasOrderChanges(false);
      setPendingOrder([]);
      await fetchVocabulary();
      console.log("Order updated successfully");
    } catch (error) {
      console.error("Order update failed:", error);
      alert("Failed to update order");
      await fetchVocabulary();
    } finally {
      setIsReordering(false);
    }
  };

  const handleReorderFromModal = async (orderPayload) => {
    try {
      setIsReordering(true);
      // Ensure payload has proper structure
      const formattedPayload = orderPayload.map(item => ({
        _id: item._id,
        no: item.no,
        order_no: item.order_no || item.no
      }));

      await ApiService.UpdateVocabularyNo(formattedPayload);
      await fetchVocabulary(); // 🔥 MUST ADD

      // Update local state
      const updatedVocabularyList = vocabularyList.map(item => {
        const updatedOrder = formattedPayload.find(p => p._id === item._id);
        if (updatedOrder && item.category === selectedCategory?.vocabulary_category) {
          return { ...item, no: updatedOrder.no, order_no: updatedOrder.order_no || updatedOrder.no };
        }
        return item;
      });

      setVocabularyList(updatedVocabularyList);
      setHasOrderChanges(false);
      setPendingOrder([]);
      console.log("Order updated successfully from modal");
    } catch (error) {
      console.error("Order update failed:", error);
      throw error;
    } finally {
      setIsReordering(false);
    }
  };

  const handleOpenOrderModal = () => {
    if (selectedCategory && filteredData.length > 0) {
      setShowOrderModal(true);
    }
  };

  return (
    <div className="p-4" id="primary-fonts">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[--text-color] mb-4">
          Vocabulary
        </h1>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Back button and header when a category is selected */}
        {selectedCategory && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToCategories}
                className="flex items-center gap-2 text-[--primary-btn-color] hover:text-[--secondary-btn-color] transition-colors"
              >
                <FaArrowLeft className="text-[--text-color-g]" />
                Back to Categories
              </button>
              <h2 className="text-xl font-semibold text-[--text-color-g]">
                {selectedCategory.vocabulary_category}
              </h2>
            </div>

          </div>
        )}

        {/* Search and Add Button Row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-6">
          {/* Search section - left side */}
          <div className="flex-1 max-w-md w-full">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={
                  selectedCategory
                    ? `Search in ${selectedCategory.vocabulary_category}...`
                    : "Search categories..."
                }
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-10 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {selectedCategory
                  ? `Found ${filteredData.length} word(s) matching "${searchTerm}"`
                  : `Found ${filteredCategories.length} category(s) matching "${searchTerm}"`
                }
              </p>
            )}
          </div>

          {/* Right side buttons group */}
          <div className="flex gap-2">
            {/* Order management buttons group */}
            <div className="flex gap-2">
              {hasOrderChanges && (
                <button
                  onClick={handleConfirmOrder}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  disabled={isReordering}
                >
                  {isReordering ? 'Saving...' : 'Confirm Order'}
                </button>
              )}
              <button
                onClick={handleOpenOrderModal}
                className="bg-[--primary-btn-color] text-white px-4 py-2 rounded-lg hover:bg-[--secondary-btn-color] transition-colors flex items-center gap-2 text-sm"
                disabled={filteredData.length === 0}
              >
                <FaList /> Manage Order
              </button>
            </div>

            {/* Add New Word button */}
            <div className="flex-shrink-0">
              <button
                onClick={() => {
                  setEditingId(null);
                  setNewWord({
                    category: selectedCategory?.vocabulary_category || "",
                    vocabulary_id: selectedCategory?.id || "",
                    word: "",
                    transliteration: "",
                    devanagari_transliteration: "",
                    english_name: "",
                    hindi_meaning: "",
                    voice_note: "",
                    meaning: "",
                  });
                  setAudioFile(null);
                  setShowModal(true);
                }}
                className="bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition duration-200 w-full md:w-auto"
                disabled={isLoading}
              >
                <FaPlus />
                Add New Word
              </button>
            </div>
          </div>
        </div>
      </div>

      {isLoading && vocabularyList.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--border-color]"></div>
        </div>
      ) : (
        <>
          {/* Category Cards View */}
          {!selectedCategory && (
            <>
              {filteredCategories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredCategories.map((category, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer border border-[--border-color]"
                      onClick={() => handleCategorySelect(category)}
                    >
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-[--text-color-g] mb-2">
                          {category.vocabulary_category}
                        </h3>
                        <p className="text-gray-600">
                          {getCategoryWordCount(category.vocabulary_category)} words
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-4">
                    {searchTerm
                      ? `No categories found matching "${searchTerm}"`
                      : "No categories available"
                    }
                  </div>
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="text-[--primary-btn-color] hover:text-[--secondary-btn-color] underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Vocabulary Cards View with Drag and Drop */}
          {selectedCategory && (
            <>
              {filteredData.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={filteredData.map(item => item._id || item.no.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {filteredData.map((item) => (
                        <SortableVocabularyItem
                          key={item._id || item.no}
                          item={item}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onPlayAudio={playAudio}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-4">
                    {searchTerm
                      ? `No words found matching "${searchTerm}" in ${selectedCategory.vocabulary_category}`
                      : `No vocabulary items found in ${selectedCategory.vocabulary_category}`
                    }
                  </div>
                  <div className="flex gap-4 justify-center">
                    {searchTerm && (
                      <button
                        onClick={clearSearch}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Clear Search
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setNewWord({
                          category: selectedCategory.vocabulary_category,
                          vocabulary_id: selectedCategory.id,
                          word: "",
                          transliteration: "",
                          devanagari_transliteration: "",
                          english_name: "",
                          hindi_meaning: "",
                          voice_note: "",
                          meaning: "",
                        });
                        setAudioFile(null);
                        setShowModal(true);
                      }}
                      className="bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition duration-200"
                    >
                      <FaPlus />
                      Add New Word
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Order Management Modal */}
      <OrderManagementModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        items={filteredData}
        categoryName={selectedCategory?.vocabulary_category}
        onReorder={handleReorderFromModal}
      />

      {/* Add/Edit Vocabulary Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-screen-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl text-[--text-color-g] font-bold mb-4">
                {editingId !== null ? "Edit Vocabulary" : "Add New Vocabulary"}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={newWord.category}
                    onChange={(e) => {
                      const selectedCat = categories.find(
                        (cat) => cat.vocabulary_category === e.target.value
                      );
                      setNewWord((prev) => ({
                        ...prev,
                        category: e.target.value,
                        vocabulary_id: selectedCat?.id || "",
                      }));
                    }}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category.vocabulary_category}>
                        {category.vocabulary_category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Word
                  </label>
                  <input
                    type="text"
                    name="word"
                    value={newWord.word}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Transliteration
                  </label>
                  <input
                    type="text"
                    name="transliteration"
                    value={newWord.transliteration}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Devanagari Transliteration
                  </label>
                  <input
                    type="text"
                    name="devanagari_transliteration"
                    value={newWord.devanagari_transliteration}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                    placeholder="Optional"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    English Name
                  </label>
                  <input
                    type="text"
                    name="english_name"
                    value={newWord.english_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                    placeholder="Optional"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Hindi Meaning
                  </label>
                  <input
                    type="text"
                    name="hindi_meaning"
                    value={newWord.hindi_meaning}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                    placeholder="Optional"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Meaning
                  </label>
                  <input
                    type="text"
                    name="meaning"
                    value={newWord.meaning}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                  />
                </div>

                {/* Audio Upload Section */}
                <div className="mb-6">
                  <label className="block text-[--text-color] mb-2">
                    Pronunciation Audio
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        disabled={isLoading || isUploading}
                      />
                    </div>

                    {audioFile && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-green-700 text-sm">
                          Selected: {audioFile.name}
                        </p>
                      </div>
                    )}

                    {newWord.voice_note && !audioFile && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-blue-700 text-sm mb-2">
                          Existing audio file:
                        </p>
                        <button
                          type="button"
                          onClick={() => playAudio(newWord.voice_note)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                        >
                          <FaVolumeUp />
                          Test current audio
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      Upload an MP3 or WAV file (max 5MB) for pronunciation
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingId(null);
                      setAudioFile(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isLoading || isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[--primary-btn-color] rounded-lg text-white hover:bg-[--secondary-btn-color] flex items-center justify-center gap-2 disabled:opacity-50"
                    disabled={isLoading || isUploading}
                  >
                    {isLoading || isUploading ? (
                      <>
                        <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        {isUploading ? "Uploading..." : "Saving..."}
                      </>
                    ) : (
                      editingId !== null ? "Update Word" : "Add Word"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vocabulary;